// Cell Renderers Components
import TextCell from '../../../components/molecules/TableCells/TextCell/TextCell';
import NumberCell from '../../../components/molecules/TableCells/NumberCell/NumberCell';
import EnumCell from '../../../components/molecules/TableCells/EnumCell/EnumCell';
import ListCell from '../../../components/molecules/TableCells/ListCell/ListCell';
import ColorCell from '../../../components/molecules/TableCells/ColorCell/ColorCell';
import TextareaCell from '../../../components/molecules/TableCells/TextareaCell/TextareaCell';
import PictureCell from '../../../components/molecules/TableCells/PictureCell/PictureCell';
import MarkdownCell from '../../../components/molecules/TableCells/MarkdownCell/MarkdownCell';
import TimeCell from '../../../components/molecules/TableCells/TimeCell/TimeCell';
import DateCell from '../../../components/molecules/TableCells/DateCell/DateCell';
import BooleanCell from '../../../components/molecules/TableCells/BooleanCell/BooleanCell';

// Direct Edit
import DirectEditWrapper from '../../../components/molecules/TableCells/DirectEditWrapper/DirectEditWrapper';
import DirectEditCellStatic from '../../../components/molecules/TableCells/DirectEditWrapper/DirectEditCellStatic';
import CellHoverPreviewWrapper from '../../../components/molecules/TableCells/CellHoverPreviewWrapper/CellHoverPreviewWrapper';
import { getEffectiveCellVerticalAlign } from '../tableCellLayoutUseCase';
import { getCellEmptyPlaceholder, getCellActiveValue } from '../../tableDirectEdit/tableDirectEditUseCase';

/**
 * @typedef {object} RenderCellByTypeMeta
 * @property {boolean} [isActive]
 * @property {boolean} [activateOnMount] - Active cell whose editor has not opened yet
 * @property {(cell: TableActiveCell) => void} [setActiveCell]
 * @property {() => void} [clearActiveCell]
 * @property {() => void} [markActiveCellOpened]
 */

/**
 * Helper function to render cell based on type
 * @param {TableCellValueObjectCustom} cellValue - Cell value with type property
 * @param {ApiManagerTableColumns} column - Column configuration
 * @param {RenderCellByTypeMeta} [meta]
 * @returns {JSX.Element | null} Rendered cell
 */
export const renderCellByType = (
  cellValue,
  column,
  meta = {},
) => {
  if (!cellValue || !cellValue.type) return null;

  const cellActiveValue = getCellActiveValue(cellValue);
  const {
    isActive = false,
    activateOnMount = false,
    setActiveCell,
    clearActiveCell,
    markActiveCellOpened,
  } = meta;

  /**
   * @param {JSX.Element} cellComponent - The cell component to wrap
   * @returns {JSX.Element} Wrapped cell component
   */
  const wrapWithHoverAndDirectEdit = (cellComponent) => {
    const handleActivate = () => {
      if (!cellValue?.id || !column?.id || !setActiveCell) return;
      setActiveCell({ rowId: cellValue.id, columnId: column.id });
    };

    const handleEditingChange = (/** @type {boolean} */ editing) => {
      if (editing) markActiveCellOpened?.();
      else clearActiveCell?.();
    };

    const directEdit = isActive ? (
      <DirectEditWrapper
        cellValue={cellValue}
        column={column}
        activateOnMount={activateOnMount}
        onEditingChange={handleEditingChange}
      >
        {cellComponent}
      </DirectEditWrapper>
    ) : (
      <DirectEditCellStatic
        cellValue={cellValue}
        onActivate={handleActivate}
      >
        {cellComponent}
      </DirectEditCellStatic>
    );

    if (cellValue?.hoverUrl) {
      return (
        <CellHoverPreviewWrapper hoverUrl={cellValue.hoverUrl}>
          {directEdit}
        </CellHoverPreviewWrapper>
      );
    }
    return directEdit;
  };
  // verticalAlign: see getEffectiveCellVerticalAlign (API may return empty string)
  const verticalAlign = getEffectiveCellVerticalAlign(cellValue);
  /**
   * @param {string} defaultPlaceholder
   * @returns {string}
   */
  const cellPlaceholder = (defaultPlaceholder) => getCellEmptyPlaceholder(
    cellValue,
    defaultPlaceholder,
  );
  const { textDecoration } = cellValue;
  const bold = textDecoration?.bold ?? false;
  const italic = textDecoration?.italic ?? false;
  const strikeThrough = textDecoration?.strikeThrough ?? false;
  const underline = textDecoration?.underline ?? false;
  switch (cellValue?.type) {
    case 'text': {
      const textValue = /** @type {string} */ (cellActiveValue ?? '');
      const cellComponent = (
        <TextCell
          value={textValue}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'textarea': {
      const textareaValue = /** @type {string} */ (cellActiveValue ?? '');
      const cellComponent = (
        <TextareaCell
          value={textareaValue}
          placeholder={cellPlaceholder('-')}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'color': {
      const colorValue = /** @type {string} */ (cellActiveValue ?? '');
      const cellComponent = (
        <ColorCell
          value={colorValue}
          placeholder={cellPlaceholder('-')}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'time': {
      const timeValue = /** @type {string} */ (cellActiveValue ?? '');
      const cellComponent = (
        <TimeCell
          value={timeValue}
          placeholder={cellPlaceholder('--:--')}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'int': {
      const intValue = /** @type {string} */ (cellActiveValue ?? 0);
      const cellComponent = (
        <NumberCell
          value={intValue}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'amount': {
      const amountValue = /** @type {string} */ (cellActiveValue ?? 0);
      const cellComponent = (
        <NumberCell
          value={amountValue}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
          formatOptions={{
            style: 'currency',
            currency: 'EUR',
          }}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'percent': {
      const percentValue = /** @type {string} */ (cellActiveValue ?? 0);
      const cellComponent = (
        <NumberCell
          value={percentValue}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
          formatOptions={{
            style: 'percent',
          }}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'enum': {
      const enumValue = /** @type {string} */ (cellActiveValue ?? '');
      const cellComponent = (
        <EnumCell
          value={enumValue}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'list': {
      const listValue = /** @type {ApiManagerTableCellArrayItemObject[]} */ (cellActiveValue ?? []);
      const cellComponent = (
        <ListCell
          value={listValue}
          placeholder={cellPlaceholder('-')}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'picture': {
      const pictureValue = /** @type {string | null} */ (cellActiveValue);
      const cellComponent = (
        <PictureCell
          src={pictureValue}
          align={cellValue.align}
          verticalAlign={verticalAlign}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'markdown': {
      const markdownValue = /** @type {string} */ (cellActiveValue ?? '');
      const cellComponent = (
        <MarkdownCell
          value={markdownValue}
          placeholder={cellPlaceholder('-')}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'date': {
      const dateValue = /** @type {string} */ (cellActiveValue ?? '');
      const cellComponent = (
        <DateCell
          value={dateValue}
          placeholder={cellPlaceholder('--')}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'datetime': {
      const datetimeValue = /** @type {string} */ (cellActiveValue ?? '');
      const cellComponent = (
        <DateCell
          value={datetimeValue}
          placeholder={cellPlaceholder('--')}
          align={cellValue.align}
          verticalAlign={verticalAlign}
          bold={bold}
          italic={italic}
          strikeThrough={strikeThrough}
          underline={underline}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    case 'boolean': {
      const booleanValue = /** @type {ApiManagerTableCellBooleanObject} */ (cellActiveValue ?? {
        value: false,
        trueIcon: 'fal fa-check',
        falseIcon: 'fal fa-close',
      });
      const cellComponent = (
        <BooleanCell
          value={booleanValue.value}
          trueIcon={booleanValue.trueIcon}
          falseIcon={booleanValue.falseIcon}
          align={cellValue.align}
          verticalAlign={verticalAlign}
        />
      );
      return wrapWithHoverAndDirectEdit(cellComponent);
    }
    default:
      return wrapWithHoverAndDirectEdit(
        <div>
          {`Unknown type : ${cellValue?.type}`}
        </div>,
      );
  }
};

/**
 * Stable TanStack cell renderer. Identity must not change when column defs are rebuilt
 * (e.g. wrapper width), otherwise React remounts every cell.
 * @param {import('@tanstack/react-table').CellContext<TableRowFlattened, TableCellValueObjectCustom>} context
 * @returns {JSX.Element | null}
 */
export function TableColumnCell({
  getValue, column, table, row,
}) {
  const cellValue = /** @type {TableCellValueObjectCustom} */ (getValue());
  const apiColumn = /** @type {TableColumnsReactTable} */ (column.columnDef);
  const tableMeta = /** @type {TableInstanceMeta | undefined} */ (table.options.meta);
  const isActive = tableMeta?.isCellActive?.(row.original.id, column.id) ?? false;
  return renderCellByType(cellValue, apiColumn, {
    isActive,
    activateOnMount: isActive && !tableMeta?.isActiveCellOpened?.(),
    setActiveCell: tableMeta?.setActiveCell,
    clearActiveCell: tableMeta?.clearActiveCell,
    markActiveCellOpened: tableMeta?.markActiveCellOpened,
  });
}

export default renderCellByType;
